import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  type Ref,
  useRef,
  useState,
  type TextareaHTMLAttributes,
} from "react";
import { api, ApiError, authHeaders, type NoteDetail } from "./api";
import { decodeBody, encodeBody, newText, type Block } from "./document";
import { Icon } from "./Icon";

type Model = { title: string; blocks: Block[] };
type Draft = { title: string; body: string; revision: number };
type Upload = {
  file: File;
  url: string;
  state: "sending" | "failed" | "ready";
  error?: string;
  retryable?: boolean;
};
const draftKey = (id: string) => `matomemo:draft:${id}`;
const message = (e: unknown) =>
  e instanceof Error
    ? e.message
    : "通信に失敗しました。もう一度お試しください。";

export type EditorHandle = { prepareToLeave: () => Promise<boolean> };

export default function Editor({
  ref,
  onSaved,
  noteId,
  onBack,
  startEditing = false,
  initialNote,
}: {
  ref?: Ref<EditorHandle>;
  onSaved?: (note: NoteDetail) => void;
  noteId: string;
  onBack: () => void;
  startEditing?: boolean;
  initialNote?: NoteDetail;
}) {
  const [note, setNote] = useState<NoteDetail | null>(initialNote ?? null);
  const noteRef = useRef<NoteDetail | null>(initialNote ?? null);
  const [model, setModel] = useState<Model>(() => ({
    title: initialNote?.title ?? "",
    blocks: decodeBody(initialNote?.body ?? ""),
  }));
  const current = useRef(model);
  const [editing, setEditing] = useState(false);
  const lease = useRef<string | null>(null);
  const sessionId = useRef(crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  const [conflictDraft, setConflictDraft] = useState<Draft | null>(null);
  const [uploads, setUploads] = useState<Record<string, Upload>>({});
  const uploadRef = useRef(uploads);
  const objectUrls = useRef(new Set<string>());
  const savingPromise = useRef<Promise<boolean> | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const activeBlock = useRef<string | null>(null);
  const textCursor = useRef<{ id: string; offset: number } | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ block: Block; index: number } | null>(
    null,
  );
  const [savedBody, setSavedBody] = useState("");
  const alive = useRef(true);
  const [retryLoad, setRetryLoad] = useState(0);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const ingesting = useRef(false);
  const [receivingFiles, setReceivingFiles] = useState(false);
  useEffect(() => {
    if (shareUrl) {
      setShareError(null);
      document.querySelector<HTMLElement>(".share-sheet")?.focus();
    }
  }, [shareUrl]);

  const remember = useCallback(
    (value: Model, revision = noteRef.current?.revision) => {
      if (!revision) return;
      try {
        localStorage.setItem(
          draftKey(noteId),
          JSON.stringify({
            title: value.title,
            body: encodeBody(
              value.blocks.filter((b) => b.type !== "image" || b.assetId),
            ),
            revision,
          }),
        );
        setStorageWarning(null);
      } catch {
        setStorageWarning("端末に下書きを保存できません。自動保存が完了するまで画面を閉じないでください。");
      }
    },
    [noteId],
  );
  const change = (next: Model) => {
    current.current = next;
    setModel(next);
    setStatus("未保存");
    remember(next);
  };
  const commitUploads = (next: Record<string, Upload>) => {
    uploadRef.current = next;
    setUploads(next);
  };
  const pending = Object.values(uploads).some((u) => u.state !== "ready");
  const dirty =
    !!note &&
    ((model.title.trim() || "無題のメモ") !== note.title ||
      encodeBody(model.blocks) !== savedBody);

  const beginEdit = useCallback(async (keepViewing = false): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      let foundConflict = false;
      const result = await api<{ leaseToken: string }>(
        `/api/notes/${noteId}/lease`,
        {
          method: "POST",
          body: JSON.stringify({ sessionId: sessionId.current }),
        },
      );
      lease.current = result.leaseToken;
      const latest = await api<NoteDetail>(`/api/notes/${noteId}`);
      if (!alive.current) return false;
      // A collaborator may have saved while this screen was being read.
      if (noteRef.current && latest.revision !== noteRef.current.revision) {
        const existing = current.current;
        if (
          existing.title !== noteRef.current.title ||
          encodeBody(existing.blocks) !==
            encodeBody(decodeBody(noteRef.current.body))
        ) {
          foundConflict = true;
          setConflictDraft({
            title: existing.title,
            body: encodeBody(existing.blocks),
            revision: noteRef.current.revision,
          });
        }
        const next = { title: latest.title, blocks: decodeBody(latest.body) };
        current.current = next;
        setModel(next);
        setSavedBody(encodeBody(next.blocks));
      }
      noteRef.current = latest;
      setNote(latest);
      if (!keepViewing) {
        setEditing(true);
        setFocusId(
          (id) => {
            if (id || !startEditing) return id;
            const first = current.current.blocks[0];
            return latest.title === "新しいメモ" ||
              !first ||
              (first.type === "text" && !first.text)
              ? "title"
              : first.id;
          },
        );
      }
      setStatus("自動保存");
      return !foundConflict;
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 409 && e.details?.holder
          ? `${e.details.holder}さんが編集中です。少し待ってからお試しください。`
          : message(e),
      );
      return false;
    } finally {
      setBusy(false);
    }
  }, [noteId, startEditing]);

  useEffect(() => {
    alive.current = true;
    let active = true;
    void (async () => {
      try {
        const value =
          initialNote && retryLoad === 0
            ? initialNote
            : await api<NoteDetail>(`/api/notes/${noteId}`);
        if (!active) return;
        noteRef.current = value;
        setNote(value);
        setSavedBody(encodeBody(decodeBody(value.body)));
        let next = {
          title:
            startEditing && value.title === "新しいメモ" ? "" : value.title,
          blocks: decodeBody(value.body),
        };
        try {
          const draft = JSON.parse(
            localStorage.getItem(draftKey(noteId)) ?? "null",
          ) as Draft | null;
          if (
            draft &&
            typeof draft.title === "string" &&
            typeof draft.body === "string" &&
            (draft.title !== value.title || draft.body !== value.body)
          ) {
            if (draft.revision === value.revision) {
              next = { title: draft.title, blocks: decodeBody(draft.body) };
              setStatus("未送信の下書きを復元しました");
            } else setConflictDraft(draft);
          }
        } catch {
          /* Storage can be disabled in private browsing. */
        }
        current.current = next;
        setModel(next);
        setError(null);
        if (startEditing && value.role !== "viewer") void beginEdit();
      } catch (e) {
        if (active) setError(message(e));
      }
    })();
    return () => {
      active = false;
      alive.current = false;
    };
  }, [noteId, startEditing, beginEdit, retryLoad, initialNote]);

  useEffect(
    () => () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  // Serialize writes. A response acknowledges only its snapshot, never subsequent input.
  const save = useCallback(
    async (release: boolean, keepalive = false): Promise<boolean> => {
      while (savingPromise.current) {
        if (!(await savingPromise.current)) return false;
        // A queued save must include input made while the previous write was pending.
        if (!release && noteRef.current &&
          (current.current.title.trim() || "無題のメモ") === noteRef.current.title &&
          encodeBody(current.current.blocks) === noteRef.current.body) return true;
      }
      if (!noteRef.current || !lease.current) return false;
      if (Object.values(uploadRef.current).some((u) => u.state !== "ready"))
        return false;
      const snapshot = current.current;
      const savedTitle = snapshot.title.trim() || "無題のメモ";
      const body = encodeBody(snapshot.blocks);
      if (body.length > 200000) {
        setError("メモが長すぎます。文章を短くしてから保存してください。");
        return false;
      }
      const revision = noteRef.current.revision;
      setSaving(true);
      setStatus("保存中…");
      const work = (async () => {
        try {
          const result = await api<{ revision: number; updatedAt: number }>(
            `/api/notes/${noteId}`,
            {
              method: "PUT",
              keepalive,
              body: JSON.stringify({
                title: savedTitle,
                body,
                revision,
                sessionId: sessionId.current,
                leaseToken: lease.current,
                release,
              }),
            },
          );
          const nextNote = {
            ...noteRef.current!,
            title: savedTitle,
            body,
            revision: result.revision,
            updatedAt: result.updatedAt,
            lease: null,
          };
          noteRef.current = nextNote;
          setNote(nextNote);
          onSaved?.(nextNote);
          setSavedBody(body);
          const unchanged = current.current === snapshot;
          if (unchanged) {
            try {
              localStorage.removeItem(draftKey(noteId));
            } catch {
              /* Server save succeeded. */
            }
            if (release && snapshot.title !== savedTitle) {
              current.current = { ...snapshot, title: savedTitle };
              setModel(current.current);
            }
          } else remember(current.current, result.revision);
          if (unchanged) setStorageWarning(null);
          setStatus(unchanged ? "保存しました" : "未保存");
          setError(null);
          if (release) {
            lease.current = null;
            activeBlock.current = null;
            textCursor.current = null;
            setFocusId(null);
            setEditing(false);
          }
          return true;
        } catch (e) {
          remember(current.current);
          setStatus("未保存");
          setError(
            e instanceof ApiError && e.status === 409
              ? "編集権が切れたか、別の変更があります。下書きを残しています。「編集を再開」で確認してください。"
              : `${message(e)} 変更は下書きに残しています。`,
          );
          if (e instanceof ApiError && e.status === 409) {
            lease.current = null;
            setEditing(false);
          }
          return false;
        } finally {
          setSaving(false);
        }
      })();
      savingPromise.current = work;
      try {
        return await work;
      } finally {
        savingPromise.current = null;
      }
    },
    [noteId, remember, onSaved],
  );

  useEffect(() => {
    if (!editing || !dirty || pending || busy || saving || error || conflictDraft) return;
    const timer = window.setTimeout(() => void save(false), 700);
    return () => clearTimeout(timer);
  }, [
    editing,
    model,
    dirty,
    pending,
    busy,
    error,
    conflictDraft,
    save,
    saving,
  ]);
  useEffect(() => {
    if (!editing) return;
    // Refresh the lease even when the user pauses typing. Choosing a photo must not end editing.
    const timer = window.setInterval(
      () => {
        if (!conflictDraft) void save(false);
      },
      3 * 60 * 1000,
    );
    const hidden = () => {
      if (document.visibilityState === "hidden" && !conflictDraft) {
        remember(current.current);
        void save(false, true);
      }
    };
    const pagehide = () => {
      remember(current.current);
      if (!conflictDraft) void save(false, true);
    };
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", pagehide);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", pagehide);
    };
  }, [editing, save, remember, conflictDraft]);
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (dirty || pending) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty, pending]);

  const updateBlock = (id: string, patch: Partial<Block>) =>
    change({
      ...current.current,
      blocks: current.current.blocks.map((b) =>
        b.id === id ? ({ ...b, ...patch, source: undefined } as Block) : b,
      ),
    });
  const insert = (block: Block, atCaret = true) => {
    const blocks = [...current.current.blocks];
    const index = blocks.findIndex((b) => b.id === activeBlock.current);
    const selected = blocks[index];
    const focused = document.activeElement;
    const cursor = !atCaret ? null : focused instanceof HTMLTextAreaElement && focused.dataset.blockId
      ? { id: focused.dataset.blockId, offset: focused.selectionStart }
      : textCursor.current;
    if (index >= 0 && selected.type === "text" && !selected.text) {
      blocks.splice(index, 1, block);
    } else if (
      index >= 0 &&
      selected.type === "text" &&
      cursor?.id === selected.id &&
      cursor.offset < selected.text.length
    ) {
      const offset = Math.max(0, cursor.offset);
      if (offset === 0) blocks.splice(index, 0, block);
      else
        blocks.splice(
          index,
          1,
          { ...selected, text: selected.text.slice(0, offset), source: undefined },
          block,
          newText(selected.text.slice(offset)),
        );
    } else blocks.splice(index >= 0 ? index + 1 : blocks.length, 0, block);
    change({ ...current.current, blocks });
    activeBlock.current = block.id;
    textCursor.current = null;
    setFocusId(block.type === "image" ? null : block.id);
  };
  const remove = (id: string) => {
    const index = current.current.blocks.findIndex((b) => b.id === id);
    const block = current.current.blocks[index];
    setUndo(
      block && (block.type !== "image" || block.assetId)
        ? { block, index }
        : null,
    );
    const blocks = current.current.blocks.filter((b) => b.id !== id);
    change({
      ...current.current,
      blocks: blocks.length ? blocks : [newText()],
    });
    const next = { ...uploadRef.current };
    delete next[id];
    commitUploads(next);
  };
  const sendImage = async (id: string, file: File) => {
    const item = uploadRef.current[id];
    if (!item) return;
    commitUploads({
      ...uploadRef.current,
      [id]: { ...item, state: "sending" },
    });
    try {
      const form = new FormData();
      form.set("file", file);
      const result = await api<{ id: string }>(`/api/notes/${noteId}/assets`, {
        method: "POST",
        body: form,
      });
      if (!alive.current || !uploadRef.current[id]) return;
      updateBlock(id, { assetId: result.id });
      commitUploads({
        ...uploadRef.current,
        [id]: { ...item, state: "ready" },
      });
    } catch (error) {
      if (alive.current && uploadRef.current[id])
        commitUploads({
          ...uploadRef.current,
          [id]: {
            ...item,
            state: "failed",
            error: message(error),
            retryable: !(error instanceof ApiError && error.status === 409),
          },
        });
    }
  };
  const uploadFiles = async (files: File[], afterBlock?: string | null) => {
    if (!files.length) return;
    if (noteRef.current?.role === "viewer") {
      setUploadError("このメモは閲覧のみです。画像を追加するには編集権限が必要です。");
      return;
    }
    if (busy || conflictDraft || ingesting.current || shareUrl) {
      setUploadError("現在の処理が終わってから画像を追加してください。");
      return;
    }
    if (files.some(file => !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type))) {
      setUploadError("JPEG・PNG・WebP・GIFの画像を選んでください。");
      return;
    }
    if (files.some(file => !file.size || file.size > 5 * 1024 * 1024)) {
      setUploadError("画像は1枚5MB以下のものを選んでください。");
      return;
    }
    const exceedsLimit = () => current.current.blocks.filter(block => block.type === "image").length + files.length > 3;
    if (exceedsLimit()) {
      setUploadError("画像は1人3枚までです。不要な画像を削除してから追加してください。");
      return;
    }
    ingesting.current = true;
    setReceivingFiles(true);
    setUploadError(null);
    try {
      if (!editing && !(await beginEdit())) return;
      if (!alive.current) return;
      if (exceedsLimit()) {
        setUploadError("画像は1人3枚までです。不要な画像を削除してから追加してください。");
        return;
      }
      if (afterBlock !== undefined) {
        activeBlock.current = afterBlock;
        textCursor.current = null;
      }
      for (const file of files) {
        const id = crypto.randomUUID();
        const url = URL.createObjectURL(file);
        objectUrls.current.add(url);
        commitUploads({ ...uploadRef.current, [id]: { file, url, state: "sending" } });
        insert({ id, type: "image", assetId: "", alt: file.name || "画像" }, afterBlock === undefined);
        void sendImage(id, file);
      }
    } finally {
      ingesting.current = false;
      setReceivingFiles(false);
    }
  };
  useImperativeHandle(ref, () => ({
    async prepareToLeave() {
      if (busy || pending || conflictDraft || ingesting.current) return false;
      setBusy(true);
      try {
        return !editing || await save(true);
      } finally {
        setBusy(false);
      }
    },
  }));
  const finish = async (back = false) => {
    if (busy || pending || conflictDraft || ingesting.current) return;
    setBusy(true);
    try {
      if ((!editing || (await save(true))) && back) onBack();
    } finally {
      setBusy(false);
    }
  };
  const share = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ shareUrl: string }>(
        `/api/notes/${noteId}/share`,
        { method: "POST" },
      );
      try {
        await navigator.clipboard.writeText(result.shareUrl);
        setToast("招待リンクをコピーしました");
        window.setTimeout(() => setToast(""), 2200);
      } catch {
        setShareUrl(result.shareUrl);
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  if (!note)
    return (
      <main className="status-screen">
        <Icon name="note" />
        <p>{error || "メモを開いています…"}</p>
        {error && (
          <button
            className="primary-button"
            onClick={() => setRetryLoad((n) => n + 1)}
          >
            もう一度試す
          </button>
        )}
      </main>
    );
  const fieldsDisabled = busy || !!conflictDraft;
  return (
    <main
      className={`editor-shell ${editing ? "is-editing" : ""} ${note.role === "viewer" ? "is-viewer" : ""}`}
      onDragEnter={(event) => {
        if (!Array.from(event.dataTransfer.types).includes("Files")) return;
        event.preventDefault();
        dragDepth.current++;
        setDragging(true);
      }}
      onDragOver={(event) => {
        if (!Array.from(event.dataTransfer.types).includes("Files")) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = note.role === "viewer" || busy || !!conflictDraft ? "none" : "copy";
      }}
      onDragLeave={(event) => {
        if (!Array.from(event.dataTransfer.types).includes("Files")) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDragging(false);
      }}
      onDrop={(event) => {
        if (!Array.from(event.dataTransfer.types).includes("Files")) return;
        event.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        const target = event.target instanceof Element
          ? event.target.closest<HTMLElement>("[data-document-block]")?.dataset.documentBlock
          : undefined;
        void uploadFiles(Array.from(event.dataTransfer.files), target ?? null);
      }}
      onKeyDown={(event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
          event.preventDefault();
          if (editing && !busy && !pending && !conflictDraft) void save(false);
        }
      }}
      onPaste={(event) => {
        const files = Array.from(event.clipboardData.files);
        if (!files.length || shareUrl) return;
        event.preventDefault();
        void uploadFiles(files);
      }}
    >
      <nav className="note-navigation" aria-label="メモの操作">
        <button className="navigation-icon" aria-label="一覧へ戻る" title="一覧へ戻る"
          disabled={busy || pending || !!conflictDraft}
          onClick={() => void finish(true)}><Icon name="back" /><span className="desktop-label">一覧</span></button>
        <span className="save-status" role="status">
          {editing ? (saving ? "保存中…" : status || "自動保存") : ""}
        </span>
        <div className="navigation-actions">
          {editing ? (
            <>
              <button className="navigation-icon" aria-label="文章を追加" title="文章を追加"
                disabled={fieldsDisabled} onPointerDown={(e) => e.preventDefault()}
                onClick={() => insert(newText())}><Icon name="text" /><span className="desktop-label">文章</span></button>
              <button className="navigation-icon" aria-label="画像" title="画像を追加"
                disabled={fieldsDisabled} onPointerDown={(e) => e.preventDefault()}
                onClick={() => fileInput.current?.click()}><Icon name="image" /><span className="desktop-label">画像</span></button>
              <button className="navigation-icon" aria-label="チェック" title="チェック項目を追加"
                disabled={fieldsDisabled} onPointerDown={(e) => e.preventDefault()}
                onClick={() => insert({ id: crypto.randomUUID(), type: "check", text: "", checked: false })}
              ><Icon name="check" /><span className="desktop-label">チェック</span></button>
            </>
          ) : (
            <>
              {note.role === "owner" && <button className="navigation-icon" aria-label="招待" title="招待"
                disabled={busy} onClick={() => void share()}><Icon name="share" /><span className="desktop-label">招待</span></button>}
              {note.role !== "viewer" && <button className="navigation-icon" aria-label={error ? "編集を再開" : "編集"} title="編集"
                disabled={busy} onClick={() => void beginEdit()}><Icon name="edit" /><span className="desktop-label">編集</span></button>}
            </>
          )}
        </div>
      </nav>
      <div className="screen-scroll editor-scroll">
        <article className="note-page">
          {editing ? (
            <AutoTextarea
              className="note-title title-input"
              aria-label="タイトル"
              placeholder="タイトル"
              value={model.title}
              maxLength={120}
              disabled={fieldsDisabled}
              focus={focusId === "title"}
              onFocused={() => setFocusId(null)}
              onChange={(e) =>
                change({ ...current.current, title: e.target.value })
              }
            />
          ) : (
            <h1
              className="note-title editable-text"
              onClick={() => {
                if (busy || note.role === "viewer" || window.getSelection()?.toString())
                  return;
                setFocusId("title");
                void beginEdit();
              }}
            >
              {model.title || "無題のメモ"}
            </h1>
          )}
          {uploadError && <div className="notice error" role="alert">{uploadError}
            <button aria-label="画像のエラーを閉じる" onClick={() => setUploadError(null)}><Icon name="close" /></button>
          </div>}
          {storageWarning && !error && <p className="notice" role="alert">{storageWarning}</p>}
          {error && (
            <div className="notice error" role="alert">
              {error}
              {editing && (
                <button
                  onClick={() => {
                    setError(null);
                    void save(false);
                  }}
                >
                  もう一度保存
                </button>
              )}
            </div>
          )}
          {note.lease && !note.lease.mine && !editing && (
            <p className="notice">
              {note.lease.displayName ?? "ほかの人"}さんが編集中です
            </p>
          )}
          {conflictDraft && (
            <div className="notice">
              <p>別の変更と重なった下書きが残っています。</p>
              <details>
                <summary>下書きを確認</summary>
                <p>{conflictDraft.title}</p>
                <p className="draft-preview">
                  {decodeBody(conflictDraft.body)
                    .map((b) => (b.type === "image" ? "［画像］" : b.text))
                    .join("\n")}
                </p>
              </details>
              {editing && (
                <button
                  onClick={() => {
                    change({
                      title: conflictDraft.title,
                      blocks: decodeBody(conflictDraft.body),
                    });
                    setConflictDraft(null);
                  }}
                >
                  下書きの内容を使う
                </button>
              )}
              <button
                onClick={() => {
                  setConflictDraft(null);
                  try {
                    localStorage.removeItem(draftKey(noteId));
                  } catch {
                    /* optional storage */
                  }
                }}
              >
                最新の内容を使う
              </button>
            </div>
          )}
          <div className="block-document">
            {model.blocks.map((block, index) => {
              const local = uploads[block.id];
              if (block.type === "image")
                return (
                  <figure
                    className="image-block"
                    data-document-block={block.id}
                    key={block.id}
                    onClick={() => {
                      activeBlock.current = block.id;
                    }}
                  >
                    {local ? (
                      <img src={local.url} alt={block.alt} />
                    ) : (
                      <PrivateImage
                        assetId={block.assetId}
                        noteId={noteId}
                        mediaToken={note.mediaToken}
                      />
                    )}
                    {editing && (
                      <button
                        className="remove-image"
                        aria-label="画像を削除"
                        disabled={fieldsDisabled}
                        onClick={() => remove(block.id)}
                      >
                        <Icon name="close" />
                      </button>
                    )}
                    {local?.state === "sending" && (
                      <figcaption className="image-state" role="status">
                        画像を送信中…
                      </figcaption>
                    )}
                    {local?.state === "failed" && (
                      <figcaption className="image-state failed" role="alert">
                        {local.error || "送信できませんでした"}
                        {local.retryable !== false && (
                          <button
                            onClick={() => void sendImage(block.id, local.file)}
                          >
                            再試行
                          </button>
                        )}
                      </figcaption>
                    )}
                  </figure>
                );
              return (
                <div
                  key={block.id}
                  data-document-block={block.id}
                  className={`text-block ${block.type === "check" ? "check-block" : ""} ${block.type === "check" && block.checked ? "is-checked" : ""}`}
                >
                  {block.type === "check" && (
                    <input
                      type="checkbox"
                      aria-label={`${block.text || "項目"}を完了`}
                      checked={block.checked}
                      disabled={fieldsDisabled || note.role === "viewer"}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (editing) {
                          updateBlock(block.id, { checked });
                          return;
                        }
                        void (async () => {
                          if (!(await beginEdit(true))) return;
                          updateBlock(block.id, { checked });
                          await save(true);
                        })();
                      }}
                    />
                  )}
                  {editing ? (
                    <AutoTextarea
                      data-block-id={block.id}
                      aria-label={
                        block.type === "check" ? "チェック項目" : "本文"
                      }
                      placeholder={
                        block.type === "check"
                          ? "やることを入力"
                          : index === 0
                            ? "ここから、自由に。"
                            : ""
                      }
                      value={block.text}
                      disabled={fieldsDisabled}
                      focus={focusId === block.id}
                      onFocused={() => setFocusId(null)}
                      onFocus={() => {
                        activeBlock.current = block.id;
                        textCursor.current = null;
                      }}
                      onSelect={(e) => {
                        textCursor.current = {
                          id: block.id,
                          offset: e.currentTarget.selectionStart,
                        };
                      }}
                      onChange={(e) =>
                        updateBlock(block.id, { text: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.nativeEvent.isComposing || e.keyCode === 229)
                          return;
                        if (
                          block.type === "check" &&
                          e.key === "Enter" &&
                          !e.shiftKey
                        ) {
                          e.preventDefault();
                          if (!block.text)
                            updateBlock(block.id, { type: "text" });
                          else {
                            activeBlock.current = block.id;
                            insert({
                              id: crypto.randomUUID(),
                              type: "check",
                              text: "",
                              checked: false,
                            });
                          }
                        } else if (
                          e.key === "Backspace" &&
                          !block.text &&
                          block.type === "check"
                        ) {
                          e.preventDefault();
                          updateBlock(block.id, { type: "text" });
                        }
                      }}
                    />
                  ) : (
                    <p
                      className={`${!block.text ? "placeholder" : ""} editable-text`}
                      onClick={(event) => {
                        if (
                          note.role === "viewer" ||
                          busy ||
                          window.getSelection()?.toString() ||
                          (event.target instanceof Element &&
                            event.target.closest("a"))
                        )
                          return;
                        activeBlock.current = block.id;
                        setFocusId(block.id);
                        void beginEdit();
                      }}
                    >
                      {block.text ? (
                        <LinkedText text={block.text} />
                      ) : model.blocks.length === 1 ? (
                        "まだ本文はありません。"
                      ) : (
                        ""
                      )}
                    </p>
                  )}

                </div>
              );
            })}
          </div>
          {note.role !== "viewer" && <p className="desktop-drop-hint"><Icon name="image" />画像をドロップ、またはコピーして貼り付け</p>}
          {undo && (
            <div className="undo-notice" role="status">
              削除しました
              <button
                onClick={() => {
                  const blocks = [...current.current.blocks];
                  blocks.splice(
                    Math.min(undo.index, blocks.length),
                    0,
                    undo.block,
                  );
                  change({ ...current.current, blocks });
                  setUndo(null);
                }}
              >
                <Icon name="undo" />
                元に戻す
              </button>
              <button aria-label="通知を閉じる" onClick={() => setUndo(null)}>
                <Icon name="close" />
              </button>
            </div>
          )}
        </article>
      </div>
      {dragging && <div className="drop-overlay" role="status">
        <Icon name="image" />
        <strong>{note.role === "viewer" ? "このメモは閲覧のみです" : busy || conflictDraft ? "現在の処理が終わるまでお待ちください" : "ここに画像をドロップ"}</strong>
        {note.role !== "viewer" && <span>JPEG・PNG・WebP・GIF ／ 1枚5MBまで</span>}
      </div>}
      {receivingFiles && <div className="toast" role="status">画像を追加しています…</div>}
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/gif"
        aria-label="追加する画像"
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          void uploadFiles(files);
          e.target.value = "";
        }}
      />
      {shareUrl && (
        <div className="sheet-backdrop" onClick={() => setShareUrl(null)}>
          <section
            className="share-sheet"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShareUrl(null);
              if (e.key === "Tab") {
                const elements = Array.from(
                  e.currentTarget.querySelectorAll<HTMLElement>("input,button"),
                );
                const index = elements.indexOf(
                  document.activeElement as HTMLElement,
                );
                if (e.shiftKey && index <= 0) {
                  e.preventDefault();
                  elements.at(-1)?.focus();
                } else if (!e.shiftKey && index === elements.length - 1) {
                  e.preventDefault();
                  elements[0]?.focus();
                }
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="share-title">一緒にメモする</h2>
            <p>
              このリンクから、メモを見たり編集できます。
              <br />
              リンクの有効期限は7日間です。
            </p>
            <input
              aria-label="共有リンク"
              value={shareUrl}
              readOnly
              onFocus={(e) => e.target.select()}
            />
            <button
              className="primary-button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  setToast("招待リンクをコピーしました");
                  window.setTimeout(() => setToast(""), 2200);
                  setShareUrl(null);
                } catch {
                  setShareError("上のリンクを長押ししてコピーしてください。");
                }
              }}
            >
              リンクをコピー
            </button>
            {shareError && <p role="alert">{shareError}</p>}
            <button className="quiet-button" onClick={() => setShareUrl(null)}>
              閉じる
            </button>
          </section>
        </div>
      )}
    </main>
  );
}

function AutoTextarea({
  focus,
  onFocused,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  focus?: boolean;
  onFocused?: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const resize = () => {
      if (ref.current) {
        // Measure a detached-from-flow copy: collapsing the live field makes
        // WebKit clamp scrollTop and move the caret when focus changes.
        const field = ref.current;
        const measure = field.cloneNode() as HTMLTextAreaElement;
        measure.removeAttribute("id");
        measure.removeAttribute("name");
        measure.setAttribute("aria-hidden", "true");
        measure.tabIndex = -1;
        measure.value = field.value;
        measure.style.cssText = `position:fixed;top:0;left:0;width:${field.getBoundingClientRect().width}px;height:0;min-height:0;visibility:hidden;pointer-events:none;`;
        field.parentElement?.appendChild(measure);
        field.style.height = `${measure.scrollHeight}px`;
        measure.remove();
      }
    };
    resize();
    let width = ref.current?.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const nextWidth = ref.current?.getBoundingClientRect().width;
      if (nextWidth !== width) {
        width = nextWidth;
        resize();
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [props.value]);
  useEffect(() => {
    if (focus) {
      ref.current?.focus({ preventScroll: true });
      onFocused?.();
    }
  }, [focus, onFocused]);
  return <textarea {...props} ref={ref} rows={1} />;
}

function PrivateImage({
  assetId,
  noteId,
  mediaToken,
}: {
  assetId: string;
  noteId: string;
  mediaToken: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setFailed(false);
    setUrl(null);
    void (async () => {
      let response = await fetch(`/api/media/${noteId}/${assetId}`, {
        headers: authHeaders(mediaToken),
        signal: controller.signal,
      });
      if (response.status === 401) {
        const refreshed = await api<NoteDetail>(`/api/notes/${noteId}`, {
          signal: controller.signal,
        });
        response = await fetch(`/api/media/${noteId}/${assetId}`, {
          headers: authHeaders(refreshed.mediaToken),
          signal: controller.signal,
        });
      }
      if (!response.ok) throw new Error("image failed");
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    })().catch(() => {
      if (!controller.signal.aborted) setFailed(true);
    });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, noteId, mediaToken, attempt]);
  return url ? (
    <img
      src={url}
      alt="メモに追加した画像"
      onError={() => {
        setUrl(null);
        setFailed(true);
      }}
    />
  ) : (
    <div className="image-placeholder">
      <Icon name="image" />
      <span>{failed ? "画像を読み込めませんでした" : "画像を読み込み中…"}</span>
      {failed && (
        <button onClick={() => setAttempt((n) => n + 1)}>再読み込み</button>
      )}
    </div>
  );
}

function LinkedText({ text }: { text: string }) {
  return text.split(/(https?:\/\/[^\s<>（）]+[\w/#=])/g).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <a key={index} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    ),
  );
}
